import React, { useContext, useEffect, useRef, useState } from 'react'
import ReactModal from 'react-modal'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { Context } from 'Context/Context'
import { ADMIN, PROFESSIONAL, FIRM, notificationsApi, markAsReadApi } from 'Constants'
import { getNotificationSeeMoreLink } from 'Utils'
import useIsMobile from 'Hooks/useIsMobile'

const DROPDOWN_LIMIT = 8;
// How long the panel stays open before we quietly mark what's showing as
// read - long enough that it doesn't feel like it's disappearing under the
// user, short enough that they don't have to click anything for it to work.
const AUTO_READ_DELAY_MS = 1500;

// The bell-icon dropdown: a quick look at recent notifications without
// leaving the page. Opening it is enough to mark what's shown as read (no
// button needed) - clicking an item marks that one read immediately and
// navigates to it, same as the full notifications page does.
function NotificationsDropdown({ uploadNew, onClose }) {
    const { userDetails, makeApiCallFunc, setNotificationCount } = useContext(Context);
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const role = userDetails?.role;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const autoReadTimerRef = useRef(null);
    const markedRef = useRef(new Set());

    const viewAllRoute =
        role === ADMIN ? "/admin/notification" :
        (role === PROFESSIONAL || role === FIRM) ? "/attorney/notification" :
        "/client/notification";

    useEffect(() => {
        if (!uploadNew || !userDetails?._id) {
            return;
        }
        setLoading(true);
        makeApiCallFunc(
            notificationsApi + "/" + userDetails._id,
            { onlyCases: false, skip: 0, limit: DROPDOWN_LIMIT },
            (data) => {
                setItems(data?.data?.notifications || []);
                setLoading(false);
            },
            () => setLoading(false),
            null,
            true,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadNew, userDetails?._id]);

    useEffect(() => {
        if (!uploadNew || loading) {
            return;
        }
        const unreadShown = items.filter((n) => !n.is_read && !markedRef.current.has(n._id));
        if (unreadShown.length === 0) {
            return;
        }
        autoReadTimerRef.current = setTimeout(() => {
            unreadShown.forEach((n) => markedRef.current.add(n._id));
            Promise.all(
                unreadShown.map((n) => new Promise((resolve) => {
                    makeApiCallFunc(
                        markAsReadApi + "/" + n._id,
                        { userId: userDetails._id, onlyCases: false },
                        () => resolve(true),
                        () => resolve(false),
                        null,
                        true,
                    );
                })),
            ).then(() => {
                setItems((prev) => prev.map((it) =>
                    unreadShown.some((u) => u._id === it._id) ? { ...it, is_read: true } : it,
                ));
                setNotificationCount((prev) => Math.max(0, (prev || 0) - unreadShown.length));
            });
        }, AUTO_READ_DELAY_MS);
        return () => clearTimeout(autoReadTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadNew, loading, items]);

    const handleItemClick = (item) => {
        if (!item.is_read && !markedRef.current.has(item._id)) {
            markedRef.current.add(item._id);
            makeApiCallFunc(
                markAsReadApi + "/" + item._id,
                { userId: userDetails._id, onlyCases: false },
                () => {},
                () => {},
                null,
                true,
            );
            setNotificationCount((prev) => Math.max(0, (prev || 0) - 1));
        }
        onClose();
        const link = getNotificationSeeMoreLink(item, role);
        if (link) {
            navigate(link);
        }
    };

    // One themed panel (ivory card, brass unread dot, muted meta) for every
    // role - previously only the client header got the themed version.
    return (
        <ReactModal
            shouldCloseOnEsc={true}
            shouldCloseOnOverlayClick={true}
            onRequestClose={onClose}
            isOpen={!!uploadNew}
            style={{
                overlay: {
                    zIndex: 50,
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                },
                content: {
                    zIndex: 50,
                    top: isMobile ? '55px' : '70px',
                    right: isMobile ? '8px' : '20px',
                    left: 'auto',
                    bottom: 'auto',
                    width: isMobile ? '92vw' : '380px',
                    maxHeight: '70vh',
                    padding: 0,
                    borderRadius: '6px',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid rgba(20,33,61,.14)',
                    boxShadow: '0 14px 34px rgba(20,33,61,.28)',
                    backgroundColor: '#fffdf8',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                },
            }}
        >
            <div className='flex items-center justify-between px-[22px] py-4 border-b border-[rgba(20,33,61,.1)]'>
                <div className='font-semibold text-[15px] text-navy'>Notifications</div>
                <div className='text-[11.5px] italic text-[rgba(20,33,61,.4)]'>Marked read on open</div>
            </div>
            <div className='overflow-y-auto' style={{ maxHeight: '52vh' }}>
                {loading ? (
                    <div className='p-8 text-center text-sm text-[rgba(20,33,61,.4)]'>Loading...</div>
                ) : items.length === 0 ? (
                    <div className='p-8 text-center text-sm text-[rgba(20,33,61,.4)]'>You're all caught up</div>
                ) : (
                    items.map((item) => (
                        <div
                            key={item._id}
                            onClick={() => handleItemClick(item)}
                            className={'flex gap-3 px-[22px] py-4 border-t border-[rgba(20,33,61,.07)] first:border-t-0 cursor-pointer hover:bg-[rgba(20,33,61,.02)] ' + (!item.is_read ? 'bg-[rgba(138,98,49,.05)]' : '')}
                        >
                            <div className={'flex-none w-2 h-2 rounded-full mt-[7px] ' + (!item.is_read ? 'bg-tan' : 'bg-transparent')} />
                            <div className='flex-1 min-w-0'>
                                <div className={'text-[14px] leading-snug ' + (!item.is_read ? 'font-semibold text-navy' : 'text-[rgba(20,33,61,.65)]')}>
                                    {item.title ? (item.caseNumber ? item.caseNumber + " - " + item.title : item.title) : "Notification"}
                                </div>
                                <div className='text-[13px] text-[rgba(20,33,61,.5)] mt-1 truncate'>{item.content}</div>
                                <div className='text-[11.5px] text-[rgba(20,33,61,.4)] mt-1.5 font-plexmono'>{moment(item.created_at).fromNow()}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <div
                className='px-4 py-3 text-center border-t border-[rgba(20,33,61,.1)] text-tan text-[13.5px] font-medium underline underline-offset-2 cursor-pointer hover:text-tanDark hover:bg-[rgba(20,33,61,.02)]'
                onClick={() => { onClose(); navigate(viewAllRoute); }}
            >
                View all
            </div>
        </ReactModal>
    );
}

export default NotificationsDropdown
